import { BaseRecord, useList } from '@refinedev/core'
import { Link } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { UUID } from 'crypto'
import { isRegenerableOpportunity } from '@/utils/opportunityRegen'

const OPPORTUNITY_FIELD_NAME_MAP: Record<string, string> = {
  uuid: 'UUID',
  criteria: 'Criteria',
  exclusions: 'Exclusions',
  fund_name: 'Fund Name',
  amount: 'Amount',
  questions: 'Questions',
  profile_embedding: 'Profile Embedding',
  profile_text: 'Profile Text',
  description: 'Description',
  application_link: 'Application Link',
  has_structured_questions: 'Structured Questions',
  opportunity_link: 'Opportunity Link',
}

const EXCLUDED_KEYS = new Set([
  'uuid',
  'actions',
  'draft_profile_text',
  'timing',
  'notes',
  'admin_comments',
  'next_check_in_date',
  'application_info',
  'references',
  'general_info',
  'amount',
  'min_amount',
  'max_amount',
  'no_min_max_amount',
  'flag_for_updates',
  'iwi',
  'funder_iwi',
  'structured_questions',
  'has_structured_questions',
  'traffic_light_status',
  'funder_active',
  'inherit_iwi_from_funder',
  'funding_rounds_error',
  'email_address',
  'funder_email_address',
])

interface FunderOpportunitiesListProps {
  funderUuid: UUID
  setOpenUpdateProfilesModal: (open: boolean) => void
  setOpportunityUUIDsToUpdate: (opportunities: UUID[]) => void
}

// The fields this component reads by name; everything else stays loosely typed.
type OpportunityRow = BaseRecord & {
  uuid: UUID
  flag_for_updates: string | null
}

interface OpportunityTooltipProps {
  opportunity: any
}

const OpportunityTooltip = ({ opportunity }: OpportunityTooltipProps) => {
  const { data: fundingRounds, isLoading: fundingRoundsLoading } = useList({
    resource: 'funding-rounds',
    meta: {
      opportunity_uuid: opportunity.uuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting funding rounds',
        description:
          error?.message || 'An error occurred while fetching funding rounds',
        type: 'error',
      }
    },
  })

  const getMissingFundingRounds = () => {
    if (opportunity.always_open) return null

    if (fundingRoundsLoading) {
      return <CircularProgress size={20} />
    }

    if (!fundingRounds?.data || fundingRounds.data.length === 0) {
      return 'Funding Rounds'
    }

    return null
  }

  const getMissingAmountData = (
    min_amount: string | null,
    max_amount: string | null,
    no_min_max_amount: boolean | null,
    amount: string | null,
  ) => {
    const noMinOrMaxAmountSpecified =
      (!min_amount || !max_amount) && !no_min_max_amount
    if (!min_amount && !max_amount && !no_min_max_amount && !amount) {
      return 'Amount Description and Data'
    } else if (amount && noMinOrMaxAmountSpecified) {
      return 'Amount Data'
    }

    return null
  }

  const getMissingContactEmail = (
    opp_email_address: string | null,
    funder_email_address: string | null,
  ) => {
    if (!opp_email_address && !funder_email_address) {
      return true
    }

    return false
  }

  const missingFields = Object.entries(opportunity)
    .filter(
      ([key, value]) =>
        !EXCLUDED_KEYS.has(key) &&
        (value === null || value === undefined || value === ''),
    )
    .map(([key]) => <li key={key}>{OPPORTUNITY_FIELD_NAME_MAP[key] || key}</li>)

  const missingFundingRounds = getMissingFundingRounds()
  const missingAmountData = getMissingAmountData(
    opportunity.min_amount,
    opportunity.max_amount,
    opportunity.no_min_max_amount,
    opportunity.amount,
  )
  const missingContactEmail = getMissingContactEmail(
    opportunity.email_address,
    opportunity.funder_email_address,
  )

  if (
    missingFields.length === 0 &&
    !missingFundingRounds &&
    !missingAmountData &&
    opportunity.has_structured_questions &&
    !missingContactEmail
  ) {
    return null
  }

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2">Missing Fields:</Typography>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {missingFields}
            {missingFundingRounds && (
              <li key="funding-rounds">{missingFundingRounds}</li>
            )}
            {missingAmountData && (
              <li key="amount-data">{missingAmountData}</li>
            )}
            {!opportunity.has_structured_questions && (
              <li key="structured-questions">Structured Questions</li>
            )}
            {missingContactEmail && (
              <li key="contact-email">
                Contact Email (Funder and Opportunity)
              </li>
            )}
          </ul>
        </Box>
      }
    >
      <InfoIcon color="error" sx={{ ml: 1, fontSize: '1.2rem' }} />
    </Tooltip>
  )
}

export default function FunderOpportunitiesList({
  funderUuid,
  setOpenUpdateProfilesModal,
  setOpportunityUUIDsToUpdate,
}: FunderOpportunitiesListProps) {
  const { data: opportunitiesData, isLoading } = useList<OpportunityRow>({
    resource: 'opportunities',
    pagination: {
      pageSize: 1000,
    },
    filters: [
      {
        field: 'funder_uuid',
        operator: 'eq',
        value: funderUuid,
      },
    ],
    errorNotification: (error) => {
      return {
        message: 'Error getting opportunities',
        description:
          error?.message || 'An error occurred while fetching opportunities',
        type: 'error',
      }
    },
  })

  if (isLoading) return <div>Loading...</div>

  if (!opportunitiesData?.data.length) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        This funder doesn't have any opportunities. Add an opportunity with the
        button below.
      </Alert>
    )
  }

  const regenerableUuids = opportunitiesData.data
    .filter(isRegenerableOpportunity)
    .map((o) => o.uuid)

  function handleOpenUpdateProfilesModal() {
    if (regenerableUuids.length === 0) return
    setOpenUpdateProfilesModal(true)
    setOpportunityUUIDsToUpdate(regenerableUuids)
  }

  return (
    <List>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip
          title={
            regenerableUuids.length === 0
              ? 'All of this funder\'s opportunities are Inactive, so there is nothing to regenerate.'
              : ''
          }
        >
          {/* Wrapper so the tooltip still fires on a disabled button. */}
          <Box component="span" sx={{ mt: -8 }}>
            <Button
              variant="contained"
              onClick={() => handleOpenUpdateProfilesModal()}
              disabled={regenerableUuids.length === 0}
              sx={{ height: '40px' }}
            >
              Update All Opportunity Profiles
            </Button>
          </Box>
        </Tooltip>
      </Box>
      {opportunitiesData &&
        [...opportunitiesData.data]
          .sort((a: any, b: any) => {
            if (!a.next_check_in_date && !b.next_check_in_date) return 0
            if (!a.next_check_in_date) return 1
            if (!b.next_check_in_date) return -1
            return (
              new Date(a.next_check_in_date).getTime() -
              new Date(b.next_check_in_date).getTime()
            )
          })
          .map((opportunity: any) => (
            <Box key={opportunity.uuid}>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={`/opportunities/${opportunity.uuid}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <ListItemText
                    primary={opportunity.fund_name || '*No fund name provided*'}
                    secondary={
                      opportunity.next_check_in_date
                        ? `Next Check-in Date: ${new Date(
                            opportunity.next_check_in_date,
                          ).toLocaleDateString('en-NZ', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}`
                        : 'No Check-in Date'
                    }
                    sx={{ width: '100%', maxWidth: 600, mr: 4 }}
                  />

                  <Box sx={{ width: '100%', mr: 4, overflow: 'hidden' }}>
                    {opportunity.admin_comments?.[0] ? (
                      <>
                        <Typography
                          variant="body1"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {opportunity.admin_comments[0].comment}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                        >
                          <strong>
                            {opportunity.admin_comments[0].from_user}
                          </strong>{' '}
                          -{' '}
                          {new Date(
                            opportunity.admin_comments[0].created_at,
                          ).toLocaleDateString('en-NZ')}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        No admin comments
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: 200,
                      minWidth: 120,
                      flexShrink: 0,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <FiberManualRecordIcon
                        sx={{
                          fontSize: '1.2rem',
                          color:
                            opportunity.traffic_light_status === 'green'
                              ? '#4caf50'
                              : opportunity.traffic_light_status === 'yellow'
                              ? '#ff9800'
                              : opportunity.traffic_light_status === 'red'
                              ? '#f44336'
                              : '#9e9e9e',
                        }}
                      />
                      <Typography variant="body1">
                        {opportunity.flag_for_updates || '—'}
                      </Typography>
                    </Box>
                    <OpportunityTooltip opportunity={opportunity} />
                  </Box>
                </ListItemButton>
              </ListItem>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
    </List>
  )
}
